/**
 * CEO report — the data contract and the pooling the report does on top of it.
 *
 * The arithmetic that every figure rests on lives in ./pool.ts, ported from the
 * published page. This file holds the three poolings that sit one grain above
 * it (geography, SKU, sub-line), the delta/tone helpers, the findings engine
 * and the internal reconciliation — all of them ports of the named function in
 * ops/report-nightly/pages/ceo.html.
 *
 * TWO RULES CARRIED FROM THAT FILE, because breaking either produces a number
 * that looks right:
 *   1. Every ratio is a ratio of sums. Pool the atoms over the window, divide
 *      once. Never average a daily percentage.
 *   2. Sub-line RUPEES are the sum of their SKUs; sub-line RATIOS are pooled
 *      from the summed atoms. Those are different operations on purpose — it is
 *      what makes the two portfolio views total to the same rupee.
 */
import {
  atomsIn,
  dayValue,
  deriveGeo,
  type Atom,
  type Basis,
  type BrandRow,
  type Pooled,
  type Reach,
} from "./pool";
import { useReport } from "../useReport";
import type { DateRange } from "@/lib/dateRange";

export const ATOMS_PATH = "/v1/reports/ceo/atoms";

/* ── the window the server resolved ───────────────────────────────────────
 * /v1/reports/* returns the full ResolvedWindow, not just {start,end,as_of}:
 * the comparison period it chose and whether that period exists at all are
 * part of it. The shared ReportResponse type declares only the three fields
 * every page uses, so the rest are read through this.
 */
export interface CeoWindow {
  start: string;
  end: string;
  as_of: string;
  previous_start?: string;
  previous_end?: string;
  /**
   * False when the previous period falls outside what the source holds. A
   * comparison against days that do not exist reads as a collapse, so the page
   * must show no delta and say why.
   */
  comparison_available?: boolean;
  note?: string;
}

export interface SkuMeta {
  product_id: string;
  sku_name: string | null;
  grammage: string | null;
  category: string | null;
  /**
   * The CLIENT'S planning line — "Core 500ml", "Zero Range". NOT YET SERVED by
   * /v1/reports/ceo/atoms (its SQL selects l2_category_name AS category and
   * nothing else), so the sub-line view falls back to `category` and says so.
   * The authority is the warehouse's portal_map.json; inventing the membership
   * here would put a third copy of the client's plan file in a third repo.
   */
  subline?: string | null;
}

/** The atoms endpoint's envelope. One fetch feeds all three CEO pages. */
export interface CeoAtoms {
  window: CeoWindow;
  /** The brand token the crawl uses — "O'cean", not "O'cean Beverages". */
  own: string;
  owner_customer_name: string;
  category: string;
  /** The curated universe name (e.g. "RTD Hydration"), when the brand has one. */
  universe_name?: string;
  /** Per-brand market share from the universe TVF (matches MCP exactly). */
  universe_market_share?: Record<string, number>;
  /** Total market size from the universe TVF. */
  universe_market_size?: number | null;
  /** Name of the pooled long-tail geography, pinned last in every ladder. */
  residual: string;
  /** Name of the pooled long-tail brand row. */
  other: string;
  national_atoms: Record<string, Atom[]>;
  geo_atoms: Record<string, Record<string, Atom[]>>;
  sku_atoms: Record<string, Atom[]>;
  sku_meta: Record<string, SkuMeta>;
  /**
   * Per-brand daily distinct listed stores and SKUs. `brandPool` takes the MEAN
   * over the window, not a sum — a store that carried the brand on all seven
   * days is one store, not seven. Absent for a brand the reach table does not
   * cover, in which case the page renders an em-dash: a brand in 2,000 stores
   * and a brand whose reach nobody measured must not look the same.
   *
   * The pooled tail ("Other") is one row a day carrying the MAX store count
   * across its brands rather than the sum, because the same dark store stocks
   * several of them and summing would put the tail above the network size.
   */
  reach?: Record<string, Reach[]>;
  network_stores: number | null;
  basis_note: string;
}

/* ── formatting and labels ────────────────────────────────────────────────
 * Port of periodLabel(f,t). Duplicated from the marketing port rather than
 * shared: ds/ is owned elsewhere and is where this belongs once it settles.
 */
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function periodLabel(
  f: string | undefined,
  t: string | undefined,
): string {
  if (!f || !t) return "—";
  const a = new Date(`${f}T00:00:00Z`);
  const b = new Date(`${t}T00:00:00Z`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()))
    return `${f} to ${t}`;
  const mo = (i: number): string => MONTHS[i] ?? "";
  return a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCFullYear() === b.getUTCFullYear()
    ? `${a.getUTCDate()}–${b.getUTCDate()} ${mo(b.getUTCMonth())} ${b.getUTCFullYear()}`
    : `${a.getUTCDate()} ${mo(a.getUTCMonth())} – ${b.getUTCDate()} ${mo(b.getUTCMonth())} ${b.getUTCFullYear()}`;
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/** Port of dowOf(d) — the weekday the chart tooltip names beside the date. */
export function dowOf(d: string): string {
  const x = new Date(`${d}T00:00:00Z`);
  return Number.isNaN(x.getTime()) ? "" : (DOW[x.getUTCDay()] ?? "");
}

export const basisLabel = (b: Basis): string => (b === "mrp" ? "MRP" : "SP");

/* ── tone ─────────────────────────────────────────────────────────────────
 * A sparkline's colour follows the DIRECTION OF THE DELTA, not the value's own
 * threshold band. The two colour systems coexist on this page and never
 * overlap: a 68% OSA is red because 68 is bad, while the line under it is green
 * if 68 is up from 61. `bandColor` in pool.ts owns the other half.
 */
export type Tone = "up" | "down" | "flat";

/** Port of toneOf(c,p,good) — the %-change tone. */
export function toneOf(
  c: number | null,
  p: number | null,
  good: "up" | "down" | "neutral",
): Tone {
  if (
    c === null ||
    p === null ||
    !p ||
    !Number.isFinite(c) ||
    !Number.isFinite(p)
  )
    return "flat";
  const ch = ((c - p) / Math.abs(p)) * 100;
  if (Math.abs(ch) < 0.05 || good === "neutral") return "flat";
  return (good === "down" ? ch < 0 : ch > 0) ? "up" : "down";
}

/** Port of ppTone(c,p) — the percentage-point tone, 0.02 pp flat band. */
export function ppTone(c: number | null, p: number | null): Tone {
  if (c === null || p === null || !Number.isFinite(c) || !Number.isFinite(p))
    return "flat";
  const d = c - p;
  return Math.abs(d) < 0.02 ? "flat" : d > 0 ? "up" : "down";
}

/**
 * Port of seriesTone(series) — first point against last, 0.5% flat band.
 *
 * Used ONLY where there is no comparison period to take a tone from. It is a
 * weaker statement than a delta and deliberately has a wider flat band.
 */
export function seriesTone(
  series: Array<number | null>,
  good: "up" | "down" = "up",
): Tone {
  const c = series.filter((v): v is number => v !== null && Number.isFinite(v));
  if (c.length < 2) return "flat";
  const a = c[0];
  const b = c[c.length - 1];
  if (a === undefined || b === undefined || !a) return "flat";
  const ch = ((b - a) / Math.abs(a)) * 100;
  if (Math.abs(ch) < 0.5) return "flat";
  return (good === "down" ? ch < 0 : ch > 0) ? "up" : "down";
}

/* ── the fetch ────────────────────────────────────────────────────────────
 * TWO FULL WINDOWS, TWO FETCHES. Every delta on this page is a second complete
 * pooling over the comparison window, exactly as the report does it — there is
 * no pre-computed previous-period figure anywhere, because the denominator of
 * market share is itself window-dependent.
 */
export interface CeoData {
  /** Is the current window still loading, and did it fail. */
  loading: boolean;
  error: Error | null;
  cur: CeoAtoms | null;
  /** Null whenever the comparison window does not exist or has not arrived. */
  prev: CeoAtoms | null;
  win: CeoWindow | undefined;
  bytes: number;
}

export function useCeoAtoms(
  customer: string,
  range: DateRange,
  category?: string,
): CeoData {
  const extra: Record<string, unknown> = category ? { category } : {};
  const q = useReport(ATOMS_PATH, customer, range, extra);
  const cur = q.data ? (q.data as unknown as CeoAtoms) : null;
  const win = cur?.window;

  /**
   * The comparison window is fetched with the CURRENT window's rival list
   * pinned. Left to its default the endpoint picks the top brands by volume
   * within whichever window it is asked about, so a brand named in one window
   * could fold into "Other" in the other — and the delta beside its name would
   * then compare it against a pooled tail.
   */
  const tracked = cur
    ? Object.keys(cur.national_atoms).filter(
        (b) => b !== cur.other && b !== cur.own,
      )
    : [];
  const canCompare =
    win?.comparison_available !== false &&
    win?.previous_start !== undefined &&
    win.previous_end !== undefined;
  const prevRange: DateRange = canCompare
    ? { start: win.previous_start, end: win.previous_end }
    : range;
  const p = useReport(
    ATOMS_PATH,
    customer,
    prevRange,
    { ...extra, tracked },
    canCompare,
  );
  const prev = canCompare && p.data ? (p.data as unknown as CeoAtoms) : null;

  return {
    loading: q.isLoading,
    error: q.error,
    cur,
    prev,
    win,
    bytes:
      (q.data?.response_meta.bytes_scanned ?? 0) +
      (p.data?.response_meta.bytes_scanned ?? 0),
  };
}

/** The window a pooled figure covers — the whole of what the server returned. */
export const span = (d: CeoAtoms): [string, string] => [
  d.window.start,
  d.window.end,
];

/* ── geography ────────────────────────────────────────────────────────────
 * Port of geoPool(f,t). Share is computed INSIDE each geography, so the brands
 * in any row total 100%; it is not a slice of the national figure.
 */
export interface GeoRow {
  name: string;
  /** True when the geography pools several cities. Unknown until reach/clusters ship. */
  isCluster: boolean;
  members: string[];
  dates: string[];
  /** Every brand's value in this geography, summed — the share denominator. */
  cat: number;
  brands: Array<{ brand: string; pooled: Pooled; share: number | null }>;
  own: Pooled | null;
  share: number | null;
  shareSeries: Array<number | null>;
  osa: number | null;
  asp: number | null;
  disc: number | null;
  units: number | null;
  rank: number;
  brandCount: number;
  leader: string | null;
  /** Share points between the leader and the own brand, inside this geography. */
  gap: number | null;
}

/** A pooled row's daily value, keyed by day rather than by position. */
function byDay(
  p: Pooled,
  series: Array<number | null>,
): Map<string, number | null> {
  const m = new Map<string, number | null>();
  p.dates.forEach((d, i) => m.set(d, series[i] ?? null));
  return m;
}

export function geoPool(
  d: CeoAtoms,
  f: string,
  t: string,
  basis: Basis,
): GeoRow[] {
  const geos = new Map<string, Map<string, Pooled>>();
  const days = new Map<string, Set<string>>();
  for (const brand of Object.keys(d.national_atoms)) {
    const byGeo = d.geo_atoms[brand];
    if (!byGeo) continue;
    for (const [name, rows] of Object.entries(byGeo)) {
      const r = atomsIn(rows, f, t);
      if (!r.length) continue;
      const g = geos.get(name) ?? new Map<string, Pooled>();
      g.set(
        brand,
        deriveGeo(
          brand,
          r,
          r.map((x) => x.d),
          basis,
        ),
      );
      geos.set(name, g);
      const ds = days.get(name) ?? new Set<string>();
      for (const x of r) ds.add(x.d);
      days.set(name, ds);
    }
  }

  const rows: GeoRow[] = [];
  for (const [name, brands] of geos) {
    // THE DATE AXIS IS THE UNION ACROSS BRANDS, not the first brand's row
    // count. The published page indexes the category series by position in
    // whichever brand it happened to see first, which misaligns the daily
    // share line whenever two brands hold a different number of days in the
    // same city. Pooled figures are unaffected either way; this only fixes the
    // sparkline's shape.
    const dates = [...(days.get(name) ?? new Set<string>())].sort();
    const own = brands.get(d.own) ?? null;

    // 14zkq6bd1nm — Rival scaling per city: own portal / own crawl ratio.
    // Lifts rival crawl GMV to portal-equivalent basis so share is like-for-like.
    let scalingRatio = 1;
    if (own && own.measured && basis !== "sp") {
      const ownCrawl = own.atoms.mrpe;
      const ownPortal = own.gmv ?? 0;
      if (ownCrawl > 0 && ownPortal > 0) scalingRatio = ownPortal / ownCrawl;
    } else if (own && own.measured && basis === "sp") {
      const ownCrawl = own.atoms.spe;
      const ownPortal = own.gmv ?? 0;
      if (ownCrawl > 0 && ownPortal > 0) scalingRatio = ownPortal / ownCrawl;
    }

    let cat = 0;
    const catByDay = new Map<string, number>();
    for (const [brand, p] of brands) {
      const isRival = brand !== d.own && brand !== d.other && !p.measured;
      const gmv = isRival ? (p.gmv ?? 0) * scalingRatio : (p.gmv ?? 0);
      cat += gmv;
      const m = byDay(p, p.gmvSeries);
      for (const day of dates)
        catByDay.set(
          day,
          (catByDay.get(day) ?? 0) +
            (m.get(day) ?? 0) * (isRival ? scalingRatio : 1),
        );
    }
    const ownByDay = own ? byDay(own, own.gmvSeries) : null;
    const ranked = [...brands.entries()]
      .filter(([b]) => b !== d.other)
      .sort((a, b) => (b[1].gmv ?? 0) - (a[1].gmv ?? 0));
    const leader = ranked[0];
    const ownShare = cat && own ? ((own.gmv ?? 0) / cat) * 100 : null;
    rows.push({
      name,
      // Cluster membership is not served by the atoms endpoint, so no
      // geography can be declared a cluster here. See the card's footnote.
      isCluster: false,
      members: [],
      dates,
      cat,
      brands: [...brands.entries()].map(([brand, pooled]) => {
        const isRival =
          brand !== d.own && brand !== d.other && !pooled.measured;
        const gmv = isRival
          ? (pooled.gmv ?? 0) * scalingRatio
          : (pooled.gmv ?? 0);
        return {
          brand,
          pooled,
          share: cat ? (gmv / cat) * 100 : null,
        };
      }),
      own,
      share: ownShare,
      shareSeries: dates.map((day) => {
        const c = catByDay.get(day) ?? 0;
        const o = ownByDay?.get(day) ?? null;
        return c && o !== null ? (o / c) * 100 : null;
      }),
      osa: own?.osa ?? null,
      asp: own?.asp ?? null,
      disc: own?.disc ?? null,
      units: own?.units ?? null,
      rank: ranked.findIndex(([b]) => b === d.own) + 1,
      brandCount: ranked.length,
      leader: leader?.[0] ?? null,
      gap:
        leader && leader[0] !== d.own && cat && own
          ? (((leader[1].gmv ?? 0) - (own.gmv ?? 0)) / cat) * 100
          : null,
    });
  }
  // The residual is pinned last: a bucket of 200-odd cities is not a league
  // position, so it must not sit in the middle of one.
  return rows.sort((a, b) => {
    if (a.name === d.residual) return 1;
    if (b.name === d.residual) return -1;
    return (b.own?.gmv ?? 0) - (a.own?.gmv ?? 0);
  });
}

/** One brand's daily OSA inside a geography, for the geo drill's spark column. */
export function osaSeriesOf(row: GeoRow, brand: string): Array<number | null> {
  const b = row.brands.find((x) => x.brand === brand);
  if (!b) return [];
  const m = byDay(b.pooled, b.pooled.series);
  return row.dates.map((d) => m.get(d) ?? null);
}

/* ── portfolio ───────────────────────────────────────────────────────────── */

export interface SkuRow extends Pooled {
  /** The product_id the atoms are keyed on. */
  productId: string;
  /** What the reader sees — the portal's own SKU name, or the id if unnamed. */
  label: string;
  grammage: string | null;
  subline: string;
}

/**
 * The sub-line a listing belongs to.
 *
 * `subline` is the client's plan line and is what the report groups on. Until
 * the endpoint serves it, the served l2 category stands in — labelled as such
 * in the card, because a row headed "Core 500ml" that is really "Hydration
 * Drinks" would be a fabricated plan line.
 */
export const hasSublines = (meta: Record<string, SkuMeta>): boolean =>
  Object.values(meta).some(
    (m) => typeof m.subline === "string" && m.subline !== "",
  );

export function sublineOf(meta: SkuMeta | undefined): string {
  return meta?.subline ?? meta?.category ?? "—";
}

export function skuPool(
  d: CeoAtoms,
  f: string,
  t: string,
  basis: Basis,
): SkuRow[] {
  const out: SkuRow[] = [];
  for (const [pid, rows] of Object.entries(d.sku_atoms)) {
    const r = atomsIn(rows, f, t);
    if (!r.length) continue;
    const meta = d.sku_meta[pid];
    const base = deriveGeo(
      pid,
      r,
      r.map((x) => x.d),
      basis,
    );
    out.push({
      ...base,
      productId: pid,
      label: meta?.sku_name ?? pid,
      grammage: meta?.grammage ?? null,
      subline: sublineOf(meta),
    });
  }
  return out.sort((a, b) => (b.gmv ?? 0) - (a.gmv ?? 0));
}

export interface SublineRow {
  name: string;
  skus: SkuRow[];
  /** SUMMED from the SKUs, never re-derived — see the file header. */
  gmv: number;
  units: number;
  atoms: Pooled["atoms"];
  dates: string[];
  gmvSeries: Array<number | null>;
  days: number;
  osa: number | null;
  asp: number | null;
  disc: number | null;
}

export function sublinePool(list: SkuRow[]): SublineRow[] {
  const by = new Map<
    string,
    {
      name: string;
      skus: SkuRow[];
      gmv: number;
      units: number;
      atoms: Pooled["atoms"];
      byDay: Map<string, number>;
    }
  >();
  for (const x of list) {
    const g = by.get(x.subline) ?? {
      name: x.subline,
      skus: [],
      gmv: 0,
      units: 0,
      atoms: { n: 0, dn: 0, pr: 0, au: 0, va: 0, eu: 0, spe: 0, mrpe: 0 },
      byDay: new Map<string, number>(),
    };
    g.skus.push(x);
    g.gmv += x.gmv ?? 0;
    g.units += x.units;
    g.atoms.n += x.atoms.n;
    g.atoms.dn += x.atoms.dn;
    g.atoms.pr += x.atoms.pr;
    g.atoms.au += x.atoms.au;
    g.atoms.va += x.atoms.va;
    g.atoms.eu += x.atoms.eu;
    g.atoms.spe += x.atoms.spe;
    g.atoms.mrpe += x.atoms.mrpe;
    // Summed on a SHARED DATE AXIS so a SKU that starts mid-period lands on the
    // right days instead of shifting the whole range's line left.
    x.dates.forEach((d, i) => {
      g.byDay.set(d, (g.byDay.get(d) ?? 0) + (x.gmvSeries[i] ?? 0));
    });
    by.set(x.subline, g);
  }
  return [...by.values()]
    .map((g) => {
      const a = g.atoms;
      const dates = [...g.byDay.keys()].sort();
      return {
        name: g.name,
        skus: g.skus,
        gmv: g.gmv,
        units: g.units,
        atoms: a,
        dates,
        gmvSeries: dates.map((d) => g.byDay.get(d) ?? null),
        days: dates.length,
        // Ratios are POOLED from the summed atoms, never averaged across the
        // SKUs: a ratio has no meaningful average, and averaging would weight a
        // SKU in forty stores the same as one in two thousand.
        osa: a.dn ? (a.n / a.dn) * 100 : null,
        asp: a.eu ? a.spe / a.eu : null,
        disc: a.mrpe ? (1 - a.spe / a.mrpe) * 100 : null,
      };
    })
    .sort((x, y) => y.gmv - x.gmv);
}

/* ── the SKU trend's four metrics ─────────────────────────────────────────
 * Port of TREND_METRICS. GMV follows the basis toggle; the other three do not,
 * because units are portal actuals and ASP/OSA are single-definition ratios.
 */
export type TrendMetric = "gmv" | "units" | "asp" | "osa";

export const TREND_METRICS: Record<
  TrendMetric,
  {
    label: string;
    kind: "money" | "int" | "price" | "pct";
    get: (r: Atom, basis: Basis) => number | null;
  }
> = {
  gmv: { label: "GMV", kind: "money", get: (r, b) => dayValue(r, b) },
  units: { label: "Units", kind: "int", get: (r) => r.au },
  asp: {
    label: "ASP",
    kind: "price",
    get: (r) => (r.eu ? r.spe / r.eu : null),
  },
  osa: {
    label: "OSA %",
    kind: "pct",
    get: (r) => (r.dn ? (r.n / r.dn) * 100 : null),
  },
};

/* ── findings ─────────────────────────────────────────────────────────────
 * Port of CEO_RULES, in order, each one free to return nothing. The count
 * badge is the number that FIRED — a findings card that always shows five
 * findings is a card nobody believes.
 *
 * THE THRESHOLDS LIVE HERE AND NOWHERE ELSE, and each finding carries the
 * figures behind it so the claim can be checked without leaving the page.
 */
export interface Finding {
  key: string;
  text: string;
  meta: string;
}

export interface MarketPool {
  brands: BrandRow[];
  other: BrandRow | null;
  size: number;
}

const money = (v: number | null): string => {
  if (v === null || !Number.isFinite(v)) return "—";
  return v >= 1e7
    ? `₹${(v / 1e7).toFixed(2)} Cr`
    : v >= 1e5
      ? `₹${(v / 1e5).toFixed(2)} L`
      : `₹${Math.round(v).toLocaleString("en-IN")}`;
};
const int = (v: number | null): string =>
  v === null || !Number.isFinite(v)
    ? "—"
    : Math.round(v).toLocaleString("en-IN");
const price = (v: number | null): string =>
  v === null || !Number.isFinite(v) ? "—" : `₹${v.toFixed(2)}`;

export interface FindingInput {
  own: string;
  networkStores: number | null;
  now: MarketPool;
  prv: MarketPool | null;
  /** SKU rupees for the two windows, for the mix-shift rule. */
  skuNow: SkuRow[];
  skuPrv: SkuRow[];
  curLabel: string;
  prevLabel: string;
}

export function findings(i: FindingInput): Finding[] {
  const out: Finding[] = [];
  const me = i.now.brands.find((b) => b.name === i.own);
  const mp = i.prv?.brands.find((b) => b.name === i.own) ?? null;

  // 1. share_move — a 0.15 pp move is the smallest that survives rounding on
  //    a two-decimal share, so anything smaller is noise, not news.
  if (me && mp && i.prv && me.share !== null && mp.share !== null) {
    const d = me.share - mp.share;
    const prevGmv = mp.gmv ?? 0;
    if (Math.abs(d) >= 0.15 && prevGmv && i.prv.size) {
      const bG = (((me.gmv ?? 0) - prevGmv) / prevGmv) * 100;
      const cG = ((i.now.size - i.prv.size) / i.prv.size) * 100;
      out.push({
        key: "share_move",
        text: `Market share ${d > 0 ? "rose" : "fell"} <b>${Math.abs(d).toFixed(2)} points to ${me.share.toFixed(2)}%</b>. ${i.own} grew ${bG.toFixed(1)}% against a market at ${cG.toFixed(1)}% — ${d > 0 ? "the gain came from outgrowing the market, not from the market growing" : "sales moved with the market but not fast enough to hold position"}.`,
        meta: `share ${mp.share.toFixed(2)}% → ${me.share.toFixed(2)}% · category ${money(i.prv.size)} → ${money(i.now.size)}`,
      });
    }
  }

  // 2. osa_vs_field — 3 pp against the BEST rival, not the average: the gap
  //    that matters is to whoever is actually on the shelf.
  if (me && me.osa !== null) {
    const others = i.now.brands.filter(
      (b) => b.name !== i.own && b.osa !== null,
    );
    const best = others.reduce<BrandRow | null>(
      (a, b) => (a === null || (b.osa ?? 0) > (a.osa ?? 0) ? b : a),
      null,
    );
    if (best?.osa != null && best.osa - me.osa >= 3) {
      out.push({
        key: "osa_vs_field",
        text: `Availability is <b>${(best.osa - me.osa).toFixed(1)} points behind ${best.name}</b> — ${me.osa.toFixed(1)}% against ${best.osa.toFixed(1)}%. On a shelf this size that gap is worth more than any pricing move.`,
        meta: `${i.own} ${Math.round(me.atoms.n)}/${Math.round(me.atoms.dn)} · ${best.name} ${Math.round(best.atoms.n)}/${Math.round(best.atoms.dn)}`,
      });
    }
  }

  // 3. assortment — no threshold: depth of assortment is the structural fact
  //    behind the ladder whenever reach covers both brands. Silent otherwise.
  if (me?.skus != null) {
    const others = i.now.brands.filter(
      (b) => b.name !== i.own && b.skus != null,
    );
    const thin = others.reduce<BrandRow | null>(
      (a, b) => (a === null || (b.skus ?? 0) < (a.skus ?? 0) ? b : a),
      null,
    );
    if (thin?.skus != null) {
      out.push({
        key: "assortment",
        text: `${i.own} runs <b>${Math.round(me.skus)} SKUs against ${thin.name}'s ${Math.round(thin.skus)}</b>, in a network where every brand reaches roughly the same ${int(i.networkStores)} stores. Depth of assortment, not distribution, is what separates the ladder here.`,
        meta: `mean distinct products listed · stores ${int(me.stores)} vs ${int(thin.stores)}`,
      });
    }
  }

  // 4. price_position — needs at least two rivals, because a "median" of one is
  //    that one rival's price wearing a statistic's name.
  if (me?.asp != null && me.disc !== null) {
    const others = i.now.brands.filter(
      (b) => b.name !== i.own && b.asp != null,
    );
    if (others.length >= 2) {
      const asp = me.asp;
      const cheaper = others.filter((b) => (b.asp ?? 0) < asp).length;
      const sorted = others.map((b) => b.asp ?? 0).sort((a, b) => a - b);
      const med = sorted[Math.floor(others.length / 2)] ?? null;
      if (med !== null) {
        out.push({
          key: "price_position",
          text: `At ${price(asp)} a unit ${i.own} sits <b>${asp > med ? "above" : "below"} the category median of ${price(med)}</b>, with ${cheaper} of ${others.length} rivals priced lower — while discounting ${me.disc.toFixed(1)}%.`,
          meta: "ASP = SUM(final_est_sales_sp)/SUM(final_est_units) · rivals estimated, crawl runs ~16% high · discount = 1 − SP/MRP value",
        });
      }
    }
  }

  // 5. mix_shift — the largest absolute move in any SKU's share of the brand's
  //    own sales. A SKU can grow in rupees while shrinking as a share.
  if (i.skuPrv.length) {
    const tn = i.skuNow.reduce((a, x) => a + (x.gmv ?? 0), 0);
    const tp = i.skuPrv.reduce((a, x) => a + (x.gmv ?? 0), 0);
    if (tn && tp) {
      let worst: { label: string; d: number; now: number } | null = null;
      for (const x of i.skuNow) {
        const p = i.skuPrv.find((y) => y.productId === x.productId);
        if (!p) continue;
        const nowPct = ((x.gmv ?? 0) / tn) * 100;
        const d = nowPct - ((p.gmv ?? 0) / tp) * 100;
        if (worst === null || Math.abs(d) > Math.abs(worst.d)) {
          worst = { label: x.label, d, now: nowPct };
        }
      }
      if (worst && Math.abs(worst.d) >= 0.5) {
        out.push({
          key: "mix_shift",
          text: `The portfolio is tilting: <b>${worst.label} moved ${worst.d > 0 ? "up" : "down"} ${Math.abs(worst.d).toFixed(1)} points to ${worst.now.toFixed(1)}%</b> of ${i.own}'s sales. Mix shifts of this size change what the brand is, not just what it earned.`,
          meta: `share of own portfolio, ${i.prevLabel} → ${i.curLabel}`,
        });
      }
    }
  }

  return out;
}

/* ── reconciliation ───────────────────────────────────────────────────────
 * Port of reconcile(), reduced to the checks the served data can actually
 * make. The published page also checks six headline figures against a stored
 * MCP contract; `contract` is not served by /v1/reports/ceo/atoms, so that half
 * is REPORTED AS NOT RUN rather than reported as a pass.
 *
 * Two of the page's three internal identities are currently identities only on
 * one basis, for reasons that live upstream of this file. They are separated
 * from the hard failures deliberately: a red "do not use these figures" banner
 * that fires on every load for a known derivation choice teaches the reader to
 * ignore the one banner that must never be ignored.
 */
export interface ReconResult {
  /** Real holes. A red banner and a refusal to stand behind the figures. */
  errors: string[];
  /** Differences that are explained rather than wrong. Stated, not hidden. */
  notes: string[];
  /** True when the stored MCP contract could not be checked at all. */
  contractNotRun: boolean;
}

export function reconcile(
  nationalOf: (basis: Basis) => Pooled | null,
  geoRowsOf: (basis: Basis) => GeoRow[],
  skusOf: (basis: Basis) => SkuRow[],
): ReconResult {
  const errors: string[] = [];
  const notes: string[] = [];

  /**
   * MRP: a true identity. Both sides are SUM(sales_value_actual) over the same
   * portal rows, grouped two ways, so anything past floating-point noise is a
   * geography that failed to bucket — which is what the residual exists to
   * prevent. A rupee, not a percentage.
   */
  const natMrp = nationalOf("mrp");
  if (natMrp) {
    const geoSum = geoRowsOf("mrp").reduce((a, r) => a + (r.own?.gmv ?? 0), 0);
    const diff = Math.abs(geoSum - (natMrp.gmv ?? 0));
    if (diff > 1) {
      errors.push(
        `MRP geographies sum to ${money(geoSum)} against a national ${money(natMrp.gmv)} (${money(diff)} apart)`,
      );
    }
  }

  /**
   * SP: NOT an identity as the national figure is currently derived. A
   * geography's SP value is its own portal units at its own crawl unit price,
   * while the national figure is the total units at the pooled national price —
   * and those agree only if the price is flat across every city. The published
   * report resolves this by overwriting the national SP with the sum of its
   * geographies; pool.ts does not, so the gap is stated with its size until it
   * does.
   */
  const natSp = nationalOf("sp");
  if (natSp) {
    const geoSum = geoRowsOf("sp").reduce((a, r) => a + (r.own?.gmv ?? 0), 0);
    const diff = Math.abs(geoSum - (natSp.gmv ?? 0));
    if (diff > 1) {
      notes.push(
        `On the SP basis the geography rows sum to ${money(geoSum)} against a national ${money(natSp.gmv)} — ${money(diff)} apart, because the national SP figure is the total units at the pooled national price while each row is its own city's price. The MRP basis is an exact identity.`,
      );
    }
  }

  /**
   * SKU units against the headline. These are on DIFFERENT BASES today: the
   * portal overlay replaces the brand's units on the national and geography
   * atoms but not on the per-SKU atoms (the portal is keyed on sku_name and the
   * SKU atoms on product_id, and the partner re-keys product_id within the day,
   * so the join is not a join anyone should do carelessly). So the SKU rows are
   * the crawl's estimate, which runs about 11.5% under the portal.
   */
  const nat = nationalOf("mrp");
  if (nat) {
    const skuUnits = skusOf("mrp").reduce((a, x) => a + x.units, 0);
    const diff = Math.abs(skuUnits - nat.units);
    if (diff > 10) {
      const pct = nat.units ? (diff / nat.units) * 100 : 0;
      notes.push(
        `The SKU rows sum to ${int(skuUnits)} units against the headline's ${int(nat.units)} — ${pct.toFixed(1)}% apart, because the portal overlay reaches the national and geography atoms but not the per-SKU atoms, so the Portfolio page is on the crawl's estimate of units and rupees.`,
      );
    }
  }

  return { errors, notes, contractNotRun: true };
}
